<script setup>
import { computed, watch, reactive, ref, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useStoreGetters, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { OnClickOutside } from '@vueuse/components';
import useVuelidate from '@vuelidate/core';

import Spinner from 'next/spinner/Spinner.vue';
import Button from 'next/button/Button.vue';
import DropdownMenu from 'next/dropdown-menu/DropdownMenu.vue';
import Breadcrumb from 'next/breadcrumb/Breadcrumb.vue';
import Editor from 'next/wgpt/Automation/editor/Index.vue';
import AutomationEditorLayout from './AutomationEditorLayout.vue';
import AutomationEditorSettings from './AutomationEditorSettings.vue';

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();
const route = useRoute();
const router = useRouter();
const automationId = computed(() => route.params.automationId);
const initialState = {};
const state = reactive({ ...initialState });
const v$ = useVuelidate({}, state);

const showActivateActionMenu = ref(false);
const now = ref(new Date());
const timer = setInterval(() => {
  now.value = new Date();
}, 1000);

onUnmounted(() => {
  clearInterval(timer);
});

const uiFlags = useMapGetter('wgptAutomations/getUIFlags');

const automation = computed(() => {
  return getters['wgptAutomations/getAutomation'].value(automationId.value);
});

const hasPendingChanges = computed(() => {
  if (!automation.value) return false;

  return (
    state.value?.name !== automation.value.name ||
    state.value?.description !== automation.value.description ||
    JSON.stringify(state.value?.steps) !==
      JSON.stringify(automation.value.steps) ||
    JSON.stringify(state.value?.trigger) !==
      JSON.stringify(automation.value.trigger)
  );
});

const isLoading = computed(() => {
  if (automation.value === automationId.value) return false;
  return uiFlags.value.isFetchingItem;
});

const isAutomationActive = computed(() => {
  return automation.value?.active;
});

const executeMenuItems = computed(() => {
  return [
    {
      label: t('WGPT_AUTOMATIONS.EDITOR.HEADER.DEACTIVATE_BUTTON'),
      value: 'deactivate',
      action: 'deactivate',
      disabled:
        isLoading.value ||
        uiFlags.value.isUpdating ||
        hasPendingChanges.value ||
        !isAutomationActive.value,
    },
  ];
});

const breadcrumbItems = computed(() => {
  const items = [
    {
      label: t('WGPT_AUTOMATIONS.EDITOR.HEADER.BREADCRUMB.AUTOMATIONS'),
      link: '#',
    },
  ];
  if (automation.value.name) {
    items.push({
      label: automation.value.name,
    });
  }
  return items;
});

watch(
  automationId,
  (newautomationId, oldautomationId) => {
    if (!newautomationId) return;
    if (newautomationId === oldautomationId) return;

    store.dispatch('wgptAutomations/show', newautomationId);
  },
  { immediate: true }
);

watch(
  automation,
  (newAutomation, oldAutomation = {}) => {
    if (Object.keys(oldAutomation).length) return;
    state.value = {
      name: newAutomation.name,
      description: newAutomation.description,
      trigger: newAutomation.trigger,
      steps: newAutomation.steps,
    };
  },
  { immediate: true }
);

const handeSaveAutomation = async () => {
  try {
    if (v$.value.$invalid) {
      v$.value.$touch();
      throw new Error(t('WGPT_AUTOMATIONS.EDITOR.API.ERROR_MESSAGE'));
    }

    const updateData = {
      id: automationId.value,
      description: state.value.description,
      name: state.value.name,
      trigger: state.value.trigger,
      steps: state.value.steps,
    };
    const updatedAutomation = await store.dispatch(
      'wgptAutomations/update',
      updateData
    );
    useAlert(t('WGPT_AUTOMATIONS.EDITOR.API.SUCCESS_MESSAGE'));

    if (updatedAutomation) {
      state.value = {
        name: updatedAutomation.name,
        description: updatedAutomation.description,
        trigger: updatedAutomation.trigger,
        steps: updatedAutomation.steps,
      };
    }
  } catch (error) {
    useAlert(error?.message || t('WGPT_AUTOMATIONS.EDITOR.API.ERROR_MESSAGE'));
  }
};

const goToAutomationsList = () => {
  if (window.history.state?.back || window.history.length > 1) {
    router.back();
  } else {
    router.push(`/app/accounts/${route.params.accountId}/contacts?page=1`);
  }
};

const toggleActivate = async active => {
  if (isAutomationActive.value === active) return;

  try {
    const id = automationId.value;
    const data = {
      active,
    };
    await store.dispatch('wgptAutomations/update', { id, ...data });
    useAlert(t('WGPT_AUTOMATIONS.EDITOR.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error.message || this.$t('WGPT_AUTOMATIONS.EDITOR.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const handleActivateAction = ({ action }) => {
  showActivateActionMenu.value = false;
  if (action === 'deactivate') {
    toggleActivate(false);
  }
};
</script>

<template>
  <AutomationEditorLayout>
    <template #start>
      <div class="flex items-center gap-5">
        <Breadcrumb :items="breadcrumbItems" @click="goToAutomationsList" />
      </div>
    </template>
    <template #end>
      <Button
        :label="t('WGPT_AUTOMATIONS.EDITOR.HEADER.SAVE_BUTTON')"
        :is-loading="uiFlags.isUpdating"
        :disabled="isLoading || uiFlags.isUpdating || !hasPendingChanges"
        size="sm"
        class="group-hover/campaign-button:brightness-110"
        @click="handeSaveAutomation"
      />

      <div class="flex items-center">
        <Button
          :label="t('WGPT_AUTOMATIONS.EDITOR.HEADER.ACTIVATE_BUTTON')"
          size="sm"
          class="ltr:rounded-r-none rtl:rounded-l-none"
          :disabled="
            isLoading ||
            uiFlags.isUpdating ||
            hasPendingChanges ||
            isAutomationActive
          "
          @click="toggleActivate(true)"
        />
        <div class="relative">
          <OnClickOutside @trigger="showActivateActionMenu = false">
            <Button
              icon="i-lucide-chevron-down"
              size="sm"
              :disabled="
                !automationId || hasPendingChanges || !isAutomationActive
              "
              class="ltr:rounded-l-none rtl:rounded-r-none"
              @click.stop="showActivateActionMenu = !showActivateActionMenu"
            />
            <DropdownMenu
              v-if="showActivateActionMenu"
              :menu-items="executeMenuItems"
              class="mt-2 ltr:right-0 rtl:left-0 top-full"
              @action="handleActivateAction($event)"
            />
          </OnClickOutside>
        </div>
      </div>

      <router-link
        :to="{ query: { selected: 'settings' } }"
        :class="{
          'settings-button-active': $route.query.selected === 'settings',
        }"
        replace
      >
        <Button
          variant="ghost"
          color="slate"
          icon="i-lucide-bolt"
          size="sm"
          class="settings-button"
        />
      </router-link>
    </template>
    <div
      v-if="isLoading"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <Editor
      v-else
      v-model="state.value"
      :dirty="hasPendingChanges"
      class="h-full border-t border-n-weak"
    >
      <template #customSidebarSettings>
        <AutomationEditorSettings v-model="state.value" />
      </template>
    </Editor>
  </AutomationEditorLayout>
</template>

<style lang="scss" scoped>
.settings-button-active .settings-button {
  @apply outline-none;
  outline-color: #fd6262;
  outline-offset: 1px;
}
</style>
